<template>
  <section class="max-w-screen-xl mx-auto">
    <!-- WITH CLINICIANS BANNER -->
    <div v-if="showCliniciansBanner"
      class="pt-3 px-14 md-max:pt-0 sm-max:px-5 font-21 md-max:text-base healthy-logos--clinicians">
      <div class="text-base text-center lg:text-21" v-html="title"></div>

      <div class="mt-6 mx-auto flex w-full max-w-[900px] flex-col gap-6 lg:flex-row lg:items-center justify-center">
        <div>
          <div
            class="flex w-full gap-6 sm-max:gap-y-3 md-max:!grid-cols-2 healthy-logos__badge-grid flex-wrap justify-center items-center overflow-hidden">
            <div class="h-[70px] xs-max:h-[54px] sm:-mr-10 xs-max:-mr-32 sm:pr-4">
              <div class="healthy-logos__clinicians-wrap h-[70px]">
                <iframe
                  data-state="max"
                  class="iframe-frontrow-sticker xs-max:!scale-[0.7]"
                  id="frontrow-sticker"
                  frameborder="0"
                  loading="lazy"
                  title="Clinicians sticker"
                  :src="frontrowIframeSrc"></iframe>
              </div>
            </div>

            <div v-for="(logo, idx) in logos" :key="idx">
              <nuxt-img
                v-if="logo.logoSrc"
                :src="logo.logoSrc"
                :alt="logo.logoAlt"
                class="w-fit max-h-[56px] sm-max:w-28"
                width="168"
                height="48"
                loading="lazy"
                format="webp" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- WITHOUT CLINICIANS -->
    <div v-else class="pt-3 px-14 md-max:pt-0 sm-max:px-5 font-21 md-max:text-base">
      <div class="text-base text-center lg:text-21" v-html="title"></div>
      <div class="grid max-w-2xl m-auto mt-6">
        <div class="grid gap-8 sm-max:gap-y-10 md-max:!grid-cols-2"
          :style="{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }">
          <div v-for="(logo, idx) in logos" :key="idx" class="w-full">
            <nuxt-img
              v-if="logo.logoSrc"
              :src="logo.logoSrc"
              :alt="logo.logoAlt"
              class="m-auto w-fit max-h-12 sm-max:w-24"
              width="140"
              height="39"
              loading="lazy"
              format="webp" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { toRef } from 'vue'
import { useFrontrowSticker } from '~/composables/useFrontrowSticker'

const props = defineProps({
  title: {
    type: String,
    default: 'Free shipping on your first order!'
  },
  columns: {
    type: Number,
    default: 4
  },
  showCliniciansBanner: {
    type: Boolean,
    default: false
  },
  defaultFrontrowId: {
    type: String,
    default: '3091'
  },
  frontrowProductMap: {
    type: String,
    default: '{}'
  },
  logos: {
    type: Array,
    default: () => [
      { logoSrc: '/assets/images/logo-1.jpg', logoAlt: 'logo-norton' },
      { logoSrc: '/assets/images/logo-2.jpg', logoAlt: 'logo-truste' },
      { logoSrc: '/assets/images/logo-3.jpg', logoAlt: 'logo-pci' },
      { logoSrc: '/assets/images/logo-4.jpg', logoAlt: 'logo-256bit' }
    ]
  }
})

const { frontrowIframeSrc } = useFrontrowSticker({
  defaultFrontrowId: toRef(props, 'defaultFrontrowId'),
  frontrowProductMap: toRef(props, 'frontrowProductMap'),
  iframeId: 'frontrow-sticker',
  enabled: toRef(props, 'showCliniciansBanner')
})

defineBuilderComponent({
  friendlyName: "HealthyLogosClinicians",
  canHaveChildren: true,
  inputs: [
    {
      name: "title",
      type: "richText",
      defaultValue: "Free shipping on your first order!"
    },
    {
      name: "columns",
      type: "number",
      defaultValue: 4,
      min: 1,
      max: 4,
      step: 1,
      helperText: "Number of columns when clinicians banner is OFF"
    },
    {
      name: "showCliniciansBanner",
      type: "boolean",
      defaultValue: false,
      helperText: "Show Frontrow Health clinicians sticker alongside the logos"
    },
    {
      name: "defaultFrontrowId",
      type: "string",
      defaultValue: "3091",
      helperText: "Frontrow product_id to show by default (loaded with page)"
    },
    {
      name: "frontrowProductMap",
      type: "longText",
      defaultValue: "{}",
      helperText: "JSON map of Step 1 product names to Frontrow product IDs. Example: {\"Grata Medium Roast\": \"3091\", \"Optimist Light Roast\": \"3092\", \"Embolden Dark Roast\": \"3090\", \"Medium Roast Decaf\": \"3093\"}"
    },
    {
      name: "logos",
      type: "list",
      defaultValue: [
        { logoSrc: "/assets/images/logo-1.jpg", logoAlt: "logo-norton" },
        { logoSrc: "/assets/images/logo-2.jpg", logoAlt: "logo-truste" },
        { logoSrc: "/assets/images/logo-3.jpg", logoAlt: "logo-pci" },
        { logoSrc: "/assets/images/logo-4.jpg", logoAlt: "logo-256bit" }
      ],
      subFields: [
        {
          name: "logoSrc",
          type: "file",
          allowedFileTypes: ["jpeg", "jpg", "png", "svg", "webp"],
          required: true,
          defaultValue: ""
        },
        {
          name: "logoAlt",
          type: "string",
          defaultValue: "logo"
        }
      ]
    }
  ]
})
</script>

<style scoped>
.iframe-frontrow-sticker {
  width: 100%;
  height: 100%;
}
</style>

<style>
/* Frontrow Health modal — script injects the iframe and toggles display.
   Keep the legacy class selector so the initial display:none hides it,
   then force fullscreen sizing with !important so the widget's inline
   width/height attributes don't shrink it back to a 500px popup. */
.iframe.frontrow {
  display: none;
  position: fixed !important;
  inset: 0 !important;
  left: 0 !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  max-width: none !important;
  max-height: none !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  z-index: 99999997 !important;
  overflow: hidden !important;
  transform: none !important;
}
</style>
